import { VBP_ENGAGEMENT_RATE_METRIC } from './reportMeasurementPeriod.util';

/** Quality measure card codes shown in Quality Measure Projections. */
export const QUALITY_MEASURE_PROJECTION_CODES = [
  'BPGC',
  'FUA_30',
  'FUA_7',
  'FUH_30',
  'FUH_7',
  'FUM_30',
  'FUM_7',
  'VBP',
] as const;

/** Quality measure card code. */
export type QualityMeasureProjectionCode =
  (typeof QUALITY_MEASURE_PROJECTION_CODES)[number];

/** Static card copy and warehouse metric lookup (one row per graph). */
export interface QualityMeasureProjectionCardConfig {
  code: QualityMeasureProjectionCode;
  /** Short label shown on the card header. */
  headline: string;
  name: string;
  description: string;
  /** Warehouse `metric` values to try (case variants). */
  warehouseMetricCandidates: readonly string[];
}

const VERIFIED_METRIC_CANDIDATES = (
  prefix: string,
  days: 7 | 30,
): readonly string[] =>
  [
    `${prefix} verified ${days} days`,
    `${prefix} Verified ${days} days`,
  ] as const;

/** Card definitions for Quality Measure Projections (warehouse-backed). */
export const QUALITY_MEASURE_PROJECTION_CARDS: readonly QualityMeasureProjectionCardConfig[] =
  [
    {
      code: 'BPGC',
      headline: 'BPGC',
      name: 'Blood Pressure Gaps Closed',
      description:
        'Members with identified blood pressure gaps closed during the measurement period.',
      warehouseMetricCandidates: ['Blood Pressure Gaps Closed'],
    },
    {
      code: 'FUA_30',
      headline: 'FUA (30-day)',
      name: 'Follow-Up After ED Visit for Substance Use',
      description:
        'Members with a follow-up visit within 30 days of an ED visit for alcohol or other drug abuse.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUA', 30),
    },
    {
      code: 'FUA_7',
      headline: 'FUA (7-day)',
      name: 'Follow-Up After ED Visit for Substance Use',
      description:
        'Members with a follow-up visit within 7 days of an ED visit for alcohol or other drug abuse.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUA', 7),
    },
    {
      code: 'FUH_30',
      headline: 'FUH (30-day)',
      name: 'Follow-Up After Hospitalization',
      description:
        'Members with a follow-up visit within 30 days after hospital discharge.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUH', 30),
    },
    {
      code: 'FUH_7',
      headline: 'FUH (7-day)',
      name: 'Follow-Up After Hospitalization',
      description:
        'Members with a follow-up visit within 7 days after hospital discharge.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUH', 7),
    },
    {
      code: 'FUM_30',
      headline: 'FUM (30-day)',
      name: 'Follow-Up After ED Visit for Mental Illness',
      description:
        'Members with a follow-up visit within 30 days of an ED visit for mental illness.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUM', 30),
    },
    {
      code: 'FUM_7',
      headline: 'FUM (7-day)',
      name: 'Follow-Up After ED Visit for Mental Illness',
      description:
        'Members with a follow-up visit within 7 days of an ED visit for mental illness.',
      warehouseMetricCandidates: VERIFIED_METRIC_CANDIDATES('FUM', 7),
    },
    {
      code: 'VBP',
      headline: 'VBP',
      name: 'VBP Engagement Rate',
      description:
        'Share of VBP-eligible members who are actively engaged in the program.',
      warehouseMetricCandidates: [VBP_ENGAGEMENT_RATE_METRIC],
    },
  ] as const;
